import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Check, Square, FileText, Image, File, ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch } from '../utils/apiUtils';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
`;

const Container = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  min-height: 0;
`;

const Footer = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Description = styled.p`
  color: #6b7280;
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const DocumentTypeSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DocumentTypeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const DocumentTypeTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  flex: 1;
`;

const DocumentCount = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  color: #6b7280;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const DocumentList = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: white;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f9fafb;
  }
`;

const DocumentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 4px;
  background: ${props => props.bgColor || '#f3f4f6'};
  color: ${props => props.color || '#6b7280'};
  flex-shrink: 0;
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
  word-break: break-word;
`;

const DocumentDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ChunkInfo = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const ToggleCheckbox = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  color: ${props => props.checked ? '#3b82f6' : '#d1d5db'};
  
  &:hover {
    background: #f3f4f6;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  
  ${props => props.primary ? `
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
    
    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const SelectAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #3b82f6;
  
  &:hover {
    background: #f0f9ff;
  }
`;

const getFileTypeIcon = (fileType) => {
  switch (fileType?.toLowerCase()) {
    case 'pdf':
      return <FileText size={16} />;
    case 'tif':
    case 'tiff':
      return <Image size={16} />;
    default:
      return <File size={16} />;
  }
};

const getFileTypeColor = (fileType) => {
  switch (fileType?.toLowerCase()) {
    case 'pdf':
      return { bgColor: '#fef2f2', color: '#dc2626' };
    case 'tif':
    case 'tiff':
      return { bgColor: '#f0f9ff', color: '#0369a1' };
    default:
      return { bgColor: '#f3f4f6', color: '#6b7280' };
  }
};

const DocumentSelectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  loanId 
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Load documents when modal opens
  useEffect(() => {
    if (isOpen && loanId) {
      loadDocuments();
    }
  }, [isOpen, loanId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      console.log('🔍 [DocumentSelectionModal] Calling API for loanId:', loanId);
      const response = await apiFetch(`/api/database/loan/${loanId}/documents`);
      console.log('🔍 [DocumentSelectionModal] Raw API response:', response);
      
      if (response.success) {
        const docs = response.data.documents || [];
        console.log('🔍 [DocumentSelectionModal] Documents from API:', docs);
        console.log('🔍 [DocumentSelectionModal] Document count:', docs.length);
        setDocuments(docs);
        
        // Initialize with all documents selected by default
        const allDocIds = new Set(docs.map(doc => doc.id));
        console.log('🔍 [DocumentSelectionModal] Selected document IDs:', allDocIds);
        setSelectedDocuments(allDocIds);
        setSelectAll(true);
      } else {
        console.error('🔍 [DocumentSelectionModal] API call failed:', response);
        setDocuments([]);
      }
    } catch (error) {
      console.error('🔍 [DocumentSelectionModal] Error loading documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Group documents by type
  const documentsByType = documents.reduce((acc, doc) => {
    const type = doc.fileType || 'unknown';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(doc);
    return acc;
  }, {});

  const toggleDocument = (docId) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocuments(newSelected);
    setSelectAll(newSelected.size === documents.length);
  };

  const toggleDocumentType = (type) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedDocuments(new Set());
      setSelectAll(false);
    } else {
      const allDocIds = new Set(documents.map(doc => doc.id));
      setSelectedDocuments(allDocIds);
      setSelectAll(true);
    }
  };

  const handleConfirm = () => {
    const selectedDocs = documents.filter(doc => selectedDocuments.has(doc.id));
    onConfirm(selectedDocs);
  };

  // Debug logging
  console.log('🔍 [DocumentSelectionModal] Render state:', {
    isOpen,
    loanId,
    documents: documents.length,
    totalAvailable: documents.length,
    totalSelected: selectedDocuments.size,
    loading
  });

  if (!isOpen) return null;

  return (
    <Overlay>
      <Container>
        <Header>
          <div>
            <Title>
              <FileText size={20} />
              Select Documents for Extraction
            </Title>
            <Description>
              Choose which documents to include in this extraction run. You can temporarily enable/disable documents to find additional values that may have been missed with previous matching priorities.
            </Description>
          </div>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Loading documents...</div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{documents.length}</strong> total documents available
                </div>
                <SelectAllButton onClick={toggleSelectAll}>
                  {selectAll ? 'Deselect All' : 'Select All'}
                </SelectAllButton>
              </div>

              {Object.entries(documentsByType).map(([type, docs]) => (
                <DocumentTypeSection key={type}>
                  <DocumentTypeHeader onClick={() => toggleDocumentType(type)}>
                    <ToggleButton>
                      {expandedTypes.has(type) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </ToggleButton>
                    <DocumentTypeTitle>
                      {type.charAt(0).toUpperCase() + type.slice(1)} Documents
                    </DocumentTypeTitle>
                    <DocumentCount>{docs.length}</DocumentCount>
                  </DocumentTypeHeader>

                  {expandedTypes.has(type) && (
                    <DocumentList>
                      {docs.map((doc) => (
                        <DocumentItem key={doc.id}>
                          <DocumentIcon {...getFileTypeColor(doc.fileType)}>
                            {getFileTypeIcon(doc.fileType)}
                          </DocumentIcon>
                          <DocumentInfo>
                            <DocumentName>{doc.fileName || doc.original_filename}</DocumentName>
                            <DocumentDetails>
                              <span>Type: {doc.fileType}</span>
                              {doc.size && <span>Size: {(doc.size / 1024 / 1024).toFixed(2)} MB</span>}
                              {doc.status && <span>Status: {doc.status}</span>}
                            </DocumentDetails>
                            {doc.fileName && doc.fileName.includes('_chunk_') && (
                              <ChunkInfo>
                                📄 This is a document chunk
                              </ChunkInfo>
                            )}
                          </DocumentInfo>
                          <ToggleCheckbox
                            checked={selectedDocuments.has(doc.id)}
                            onClick={() => toggleDocument(doc.id)}
                          >
                            {selectedDocuments.has(doc.id) ? <Check size={16} /> : <Square size={16} />}
                          </ToggleCheckbox>
                        </DocumentItem>
                      ))}
                    </DocumentList>
                  )}
                </DocumentTypeSection>
              ))}
            </>
          )}
        </Content>

        <Footer>
          <div>
            <strong>{selectedDocuments.size}</strong> of <strong>{documents.length}</strong> documents selected
          </div>
          <ActionButtons>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              primary 
              onClick={handleConfirm}
              disabled={selectedDocuments.size === 0}
            >
              Continue with Selected Documents
            </Button>
          </ActionButtons>
        </Footer>
      </Container>
    </Overlay>
  );
};

export default DocumentSelectionModal;
